import * as React from 'react';
import Modal from '@material-ui/core/Modal';
import clsx from 'clsx';
import { THEME } from 'GlobalStyles';
import { FPCardContent, FPCardHeader } from 'UI/components/atoms/FPCard';
import FPIconButton from 'UI/components/atoms/FPIconButton';
import FooterActionsControlsLegacy from 'UI/components/molecules/FooterActionsControlsLegacy';
import { CloseIcon } from 'UI/res';

import { useStyles } from './styles';

/** @typedef {import('@material-ui/core').ModalProps} ModalProps */

/**
 * @typedef {('tight'|'regular'|'relaxed')} SpacingVariant
 */

/**
 * @param {Object} props
 * @param {React.ReactNode} props.additionalHeaderActions
 * @param {Function} props.onClose
 */
const HeaderActions = ({ additionalHeaderActions, onClose }) => {
  return (
    <>
      {additionalHeaderActions}
      <FPIconButton onClick={onClose} tooltipProps={{ placement: 'bottom', title: 'Close' }}>
        <CloseIcon fill={THEME.palette.grey[600]} />
      </FPIconButton>
    </>
  );
};

/**
 * @param {Object} props
 * @param {React.ReactNode} [props.children]
 * @param {Function} props.onClose
 * @param {string} [props.title='']
 * @param {Object} [props.primaryProps]
 * @param {Object} [props.titleProps]
 * @param {Object} [props.headerProps]
 * @param {React.ReactNode} [props.additionalHeaderActions]
 * @param {boolean} [props.hasFooterActions=true]
 * @param {Object} [props.contentProps]
 * @param {boolean} [props.fitContent=false]
 * @param {React.ReactNode} [props.footer]
 * @param {SpacingVariant} [props.contentVariant='regular']
 * @param {SpacingVariant} [props.footerVariant='relaxed']
 * @param {string} [props.footerClassName]
 * @param {Object} [props.customClasses]
 * @param {Object} [props.secondaryProps]
 * @param {boolean} [props.saveAction=true]
 */
const FPModal = ({
  title,
  children,
  onClose,
  primaryProps,
  titleProps,
  headerProps,
  additionalHeaderActions,
  hasFooterActions,
  contentProps,
  fitContent,
  footer,
  contentVariant,
  footerVariant,
  footerClassName,
  customClasses,
  secondaryProps,
  saveAction,
  ...rest
}) => {
  const classes = useStyles({ fitContent });
  return (
    <Modal onClose={onClose} {...rest}>
      <div className={clsx(classes.modalContainer, customClasses)}>
        <FPCardHeader
          title={title}
          action={
            <HeaderActions additionalHeaderActions={additionalHeaderActions} onClose={onClose} />
          }
          className={titleProps.backNavigation ? classes.headerWithArrow : classes.header}
          {...headerProps}
        />
        <FPCardContent
          variant={contentVariant}
          className={classes.contentContainer}
          {...contentProps}
        >
          {children}
        </FPCardContent>
        {hasFooterActions && (
          <FPCardContent
            variant={footerVariant}
            className={clsx(classes.footerContainer, footerClassName)}
          >
            {footer || (
              <FooterActionsControlsLegacy
                secondaryAction={onClose}
                primaryProps={primaryProps}
                secondaryProps={secondaryProps}
                saveAction={saveAction}
              />
            )}
          </FPCardContent>
        )}
      </div>
    </Modal>
  );
};

FPModal.defaultProps = {
  title: '',
  saveAction: true,
  titleProps: {},
  secondaryProps: {},
  primaryProps: undefined,
  additionalHeaderActions: null,
  headerProps: {},
  fitContent: false,
  hasFooterActions: true,
  contentVariant: 'regular',
  footerVariant: 'relaxed',
  footerClassName: null,
  footer: null,
  customClasses: undefined
};

export default FPModal;
